import Reveal from './Reveal';
import { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const FAQ_ITEMS = [
  // ── General ───────────────────────────────────────────────────
  {
    category: 'General',
    q: 'Can I join if I have never played competitive volleyball?',
    a: 'Absolutely. Our drop-ins welcome all skill levels, from total beginners to advanced players. We organise balanced courts so everyone gets a good game.',
  },
  {
    category: 'General',
    q: 'What is the difference between a drop-in and a tournament?',
    a: 'Drop-ins are casual sessions : show up, pay, and play. No team required. Tournaments are structured events where teams register and compete across pool play and a playoff bracket for the OVR Cup or Blitz crown.',
  },
  {
    category: 'General',
    q: 'Where are events held?',
    a: 'All OVR events take place in Ottawa, Ontario. Specific venue details are shared in your confirmation email after registration.',
  },
  {
    category: 'General',
    q: 'How do I stay updated on new events?',
    a: 'Follow @ovrleague and @horizoneventsofficial on Instagram for real-time announcements, schedules, and exclusive early-access drops.',
  },
  {
    category: 'General',
    q: 'What should I bring?',
    a: 'Athletic clothes, volleyball knee pads if you have them, and water. Knee pads are strongly recommended for indoor play. Jewellery must be removed before play.',
  },
  {
    category: 'General',
    q: 'What is the co-ed rule?',
    a: 'OVR events are co-ed. A minimum of 2 women must be on the court at all times during play.',
  },

  // ── Drop-Ins ──────────────────────────────────────────────────
  {
    category: 'Drop-Ins',
    q: 'How do drop-in sessions work?',
    a: 'Register and pay online, show up at the venue, and you are in. We assign you to balanced courts on the day. Sessions run on a rotating schedule so you get maximum court time.',
  },
  {
    category: 'Drop-Ins',
    q: 'Can I transfer my drop-in spot to a friend?',
    a: 'Yes. If you can no longer make it, your spot can be transferred to a friend at any time. Message us on Instagram with their info and we will swap them in.',
  },
  {
    category: 'Drop-Ins',
    q: 'What happens if a drop-in session is full?',
    a: 'You can join the waitlist. If a spot opens up you will be notified automatically. Spots free up regularly as people transfer or cancel.',
  },

  // ── Tournaments ───────────────────────────────────────────────
  {
    category: 'Tournaments',
    q: 'How do I register for a tournament?',
    a: 'Use the Tournaments tab on this site to view upcoming seasons and sign up your team or as a free agent. Spots are limited so register early.',
  },
  {
    category: 'Tournaments',
    q: 'Can I join a tournament without a full team?',
    a: 'Yes. Register as a free agent and we will place you on a team that needs players. Free agents who are not placed on a team in time are refunded automatically.',
  },
  {
    category: 'Tournaments',
    q: 'How many players are on a team?',
    a: 'Teams field 6 players on the court. We recommend registering 7–8 players to have subs available throughout the day.',
  },
  {
    category: 'Tournaments',
    q: 'When do I find out my schedule?',
    a: 'Match schedules are shared before tournament day via email and posted on our Instagram. Pool assignments are finalised once registration closes.',
  },
  {
    category: 'Tournaments',
    q: 'How do standings and tiebreakers work?',
    a: 'Teams earn 2 points for a win and 0 for a loss. Tiebreakers are applied in this order: (1) head-to-head result, (2) sets won, (3) point differential.',
  },
  {
    category: 'Tournaments',
    q: 'Are there prizes for winning?',
    a: 'Yes. OVR Cup and Blitz champions receive trophies, medals, and bragging rights. Awards are presented immediately after the final with a team photo and MVP recognition.',
  },

  // ── OVR Cup ───────────────────────────────────────────────────
  {
    category: 'OVR Cup',
    q: 'What is the OVR Cup format?',
    a: '10 teams split into Pool A and Pool B (5 teams each). Every team plays 4 pool games (round-robin within their pool). Top 4 from each pool advance to single-elimination playoffs. 5th place in each pool is eliminated.',
  },
  {
    category: 'OVR Cup',
    q: 'What is the OVR Cup pool play format?',
    a: '1 set to 25 points. 25-minute time cap per match. No win-by-2 rule in pool play, first to 25 wins.',
  },
  {
    category: 'OVR Cup',
    q: 'What is the OVR Cup quarter-final format?',
    a: '1 set to 25. No time cap. Cross-pool seeding: A1 vs B4, A2 vs B3, B1 vs A4, B2 vs A3. Top seeds from one pool play lower seeds from the other, no early rematches.',
  },
  {
    category: 'OVR Cup',
    q: 'What is the OVR Cup semi-final and final format?',
    a: 'Best of 3 sets: 25 / 25 / 15. Win by 2, capped at 30. No time limit.',
  },
  {
    category: 'OVR Cup',
    q: 'Is there a bronze medal match in the OVR Cup?',
    a: 'No. The OVR Cup goes straight from two semi-finals to the championship final. No third-place game.',
  },

  // ── Blitz ─────────────────────────────────────────────────────
  {
    category: 'Blitz',
    q: 'What is the Blitz format?',
    a: '4 teams play a full round-robin (3 rounds, 2 matches per round, everyone plays everyone). Then both the Gold Final and Bronze Final run simultaneously. Compact, fast, and intense.',
  },
  {
    category: 'Blitz',
    q: 'What is the Blitz pool play format?',
    a: '1 set to 25 points. 20-minute hard clock per match. Rally scoring continues but the match ends when the clock hits zero regardless of score. Cap at 27.',
  },
  {
    category: 'Blitz',
    q: 'What is the Blitz finals format?',
    a: 'Best of 3 sets: 25 / 25 / 15. Win by 2, capped at 30. No time limit. Gold Final (1st vs 2nd) and Bronze Final (3rd vs 4th) run side-by-side.',
  },
  {
    category: 'Blitz',
    q: 'How are Blitz seedings determined for the finals?',
    a: 'Final standings after the 3-round round-robin determine seeds. 1st plays 2nd in the Gold Final, 3rd plays 4th in the Bronze Final.',
  },

  // ── Payments & Refunds ────────────────────────────────────────
  {
    category: 'Payments & Refunds',
    q: 'What is the cancellation and refund policy?',
    a: 'Tournament teams must cancel at least 5 days before the event for a full refund. Free agents must cancel at least 2 days before. Drop-ins must cancel at least 24 hours before the session. All refunds are minus payment processing fees. Free agents not placed on a team are refunded automatically.',
  },
  {
    category: 'Payments & Refunds',
    q: 'How long do refunds take?',
    a: 'Refunds are processed back to your original payment method within 5–10 business days.',
  },
  {
    category: 'Payments & Refunds',
    q: 'What payment methods are accepted?',
    a: 'We accept all major credit and debit cards through our secure checkout. No cash payments at the door.',
  },
  {
    category: 'Payments & Refunds',
    q: 'I did not receive my confirmation email. What should I do?',
    a: 'Check your spam folder and mark the email from noreply@ovrleague.com as Not Spam. If it is not there within a few minutes of registering, message us on Instagram and we will confirm your spot manually.',
  },
];

const CATEGORIES = ['All', ...Array.from(new Set(FAQ_ITEMS.map(f => f.category)))];

// ── Non-English detection ─────────────────────────────────────
const NON_ENGLISH_MSGS = [
  { emoji: '💀', line1: "bro really typed in another language", line2: "our courts are in Ottawa, not the UN" },
  { emoji: '🏐', line1: "the only language we speak here", line2: "is spike, block, and dig" },
  { emoji: '🤔', line1: "respectfully, we have no idea what that says", line2: "try it in English and we'll try our best" },
  { emoji: '🌍', line1: "google translate is that way →", line2: "the Ottawa courts are this way ←" },
  { emoji: '😭', line1: "our referee doesn't speak that language", line2: "neither does anyone else on the team lol" },
  { emoji: '🛂', line1: "typing in another language won't open spots faster", line2: "trust us, we checked" },
];

// ── Easter egg keywords ───────────────────────────────────────
const EASTER_EGGS = [
  { keys: ['blitz'], emoji: '⚡', msg: "now you're speaking our language. fastest format in Ottawa." },
  { keys: ['champion', 'champions', 'win', 'winner'], emoji: '🏆', msg: "manifesting already? we respect that energy." },
  { keys: ['refund'], emoji: '😬', msg: "yikes. we hope it doesn't come to that. check the policy below." },
  { keys: ['spike', 'spikes'], emoji: '🏐', msg: "a fellow person of culture. welcome." },
  { keys: ['ovr'], emoji: '👀', msg: "you rang? that's us. hi." },
  { keys: ['ottawa'], emoji: '🇨🇦', msg: "finally, someone who knows where we are. Ottawa represent." },
  { keys: ['money', 'price', 'cost', 'cheap', 'expensive'], emoji: '💸', msg: "we prefer the term 'investment in your volleyball career'." },
  { keys: ['love', 'volleyball'], emoji: '❤️', msg: "same. that's literally why we started this." },
  { keys: ['party', 'parties'], emoji: '🎉', msg: "not that kind of party. Block Party the team though? they're real." },
  { keys: ['help', 'help me'], emoji: '🆘', msg: "we got you. DM @ovrleague on Instagram if nothing here answers your question." },
];

// ── Search placeholder rotation ───────────────────────────────
const PLACEHOLDERS = [
  'Search questions…',
  'Ask us anything…',
  'Refunds? Formats? Blitz rules?',
  'Type a word, find an answer…',
  'Registration, formats, schedules…',
];

function isNonEnglish(str) {
  // Anything outside basic Latin + Latin Extended (accented chars still ok)
  return /[^ -ÿ]/.test(str); // eslint-disable-line no-control-regex
}

function getEasterEgg(q) {
  const lower = q.trim().toLowerCase();
  return EASTER_EGGS.find(e => e.keys.some(k => lower === k || lower.includes(k))) || null;
}

function highlight(text, query) {
  if (!query.trim()) return text;
  try {
    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? <mark key={i} style={{ background: 'rgba(157,21,21,0.35)', color: '#fff', borderRadius: 2, padding: '0 1px' }}>{part}</mark>
        : part
    );
  } catch {
    return text;
  }
}

function FAQItem({ item, isOpen, onToggle, query }) {
  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <button
        onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '20px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 500, color: isOpen ? '#fff' : 'rgba(255,255,255,0.8)', lineHeight: 1.4, transition: 'color 0.2s ease' }}>
          {highlight(item.q, query)}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.22, ease: 'easeInOut' }}
          style={{ flexShrink: 0, color: isOpen ? '#9D1515' : 'rgba(255,255,255,0.3)' }}
        >
          <ChevronDown size={18} />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, margin: '0 0 20px 0', paddingRight: 28 }}>
              {highlight(item.a, query)}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const nonEnglishMsgRef = useRef(Math.floor(Math.random() * NON_ENGLISH_MSGS.length));

  // Rotate placeholder
  useEffect(() => {
    const id = setInterval(() => setPlaceholderIdx(i => (i + 1) % PLACEHOLDERS.length), 3500);
    return () => clearInterval(id);
  }, []);

  // Pick a new non-English message each time a non-English string is entered
  useEffect(() => {
    if (isNonEnglish(query)) {
      nonEnglishMsgRef.current = Math.floor(Math.random() * NON_ENGLISH_MSGS.length);
    }
  }, [query]);

  const nonEnglish = query.trim().length > 0 && isNonEnglish(query);
  const easterEgg = !nonEnglish && query.trim().length >= 3 ? getEasterEgg(query) : null;

  const filtered = useMemo(() => {
    if (nonEnglish) return [];
    const q = query.trim().toLowerCase();
    return FAQ_ITEMS
      .map((item, i) => ({ ...item, originalIndex: i }))
      .filter(item => {
        const catMatch = activeCategory === 'All' || item.category === activeCategory;
        if (!catMatch) return false;
        if (!q) return true;
        return item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q);
      });
  }, [query, activeCategory, nonEnglish]);

  const toggle = (idx) => setOpenIndex(prev => prev === idx ? null : idx);

  const msg = NON_ENGLISH_MSGS[nonEnglishMsgRef.current];

  return (
    <section id="faq" style={{ background: '#0a0a0a', padding: '80px 0 60px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '0 24px' }}>

        {/* Header */}
        <Reveal style={{ marginBottom: 36 }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#9D1515', margin: '0 0 12px 0' }}>
            Got Questions?
          </p>
          <h2 style={{ fontFamily: "'Bebas Neue', 'Oswald', sans-serif", fontSize: 'clamp(36px, 5vw, 60px)', fontWeight: 900, color: '#fff', letterSpacing: '0.05em', margin: 0 }}>
            FAQs
          </h2>
        </Reveal>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder={PLACEHOLDERS[placeholderIdx]}
            value={query}
            onChange={e => { setQuery(e.target.value); setOpenIndex(null); }}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${nonEnglish ? 'rgba(157,21,21,0.5)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 4,
              padding: '12px 14px 12px 40px',
              fontFamily: 'Inter, sans-serif',
              fontSize: 14,
              color: '#fff',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={e => { if (!nonEnglish) e.target.style.borderColor = '#9D1515'; }}
            onBlur={e => e.target.style.borderColor = nonEnglish ? 'rgba(157,21,21,0.5)' : 'rgba(255,255,255,0.1)'}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
            >×</button>
          )}
        </div>

        {/* Non-English quirky message */}
        <AnimatePresence>
          {nonEnglish && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(157,21,21,0.07)', border: '1px solid rgba(157,21,21,0.25)', borderRadius: 4, display: 'flex', alignItems: 'flex-start', gap: 14 }}
            >
              <span style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>{msg.emoji}</span>
              <div>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{msg.line1}</div>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>{msg.line2}</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Easter egg banner */}
        <AnimatePresence>
          {easterEgg && (
            <motion.div
              key={easterEgg.emoji}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
              style={{ marginBottom: 20, padding: '12px 18px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 12 }}
            >
              <span style={{ fontSize: 20, lineHeight: 1 }}>{easterEgg.emoji}</span>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>{easterEgg.msg}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Category pills */}
        {!nonEnglish && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 32 }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
                style={{
                  background: activeCategory === cat ? '#9D1515' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${activeCategory === cat ? '#9D1515' : 'rgba(255,255,255,0.1)'}`,
                  color: activeCategory === cat ? '#fff' : 'rgba(255,255,255,0.55)',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 500,
                  padding: '6px 14px',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  letterSpacing: '0.04em',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        {!nonEnglish && (
          filtered.length === 0 && query.trim() ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ textAlign: 'center', padding: '52px 0' }}
            >
              <div style={{ fontSize: 36, marginBottom: 12 }}>🤷</div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
                nothing matched <span style={{ color: '#9D1515' }}>"{query}"</span>
              </div>
              <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>
                that's a new one. try a different word, or DM us on Instagram.
              </div>
            </motion.div>
          ) : (
            <Reveal delay={120}>
              {filtered.map((item) => (
                <FAQItem
                  key={item.originalIndex}
                  item={item}
                  isOpen={openIndex === item.originalIndex}
                  onToggle={() => toggle(item.originalIndex)}
                  query={query}
                />
              ))}
            </Reveal>
          )
        )}

        {/* Footer note */}
        <div style={{ marginTop: 40, padding: '16px 20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4 }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.65, margin: 0 }}>
            Confirmation emails are sent from{' '}
            <strong style={{ color: 'rgba(255,255,255,0.6)' }}>noreply@ovrleague.com</strong>.
            {' '}If you don't see yours, check spam and mark it{' '}
            <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Not Spam</strong> so future emails land in your inbox.
          </p>
        </div>
      </div>
    </section>
  );
}
